import React, { useEffect, useState } from "react";
import {
  FieldType,
  FieldTypeType,
  ReportFieldType,
  SidebarMenuItemType,
  TabType,
  Unit,
} from "../../../services/helpers/types";
import Fields from "../Fields/Fields";
import DoctorsReport from "../../molecules/DoctorsReport/DoctorsReport";
import Tabs from "../../molecules/Tabs/Tabs";
import { bindFieldsToReport } from "./helpers";
import Button from "../../atoms/Button/Button";
import RestoreIcon from "@mui/icons-material/Restore";
import FormTitle from "../../atoms/FormTitle/FormTitle";
import useIsEditMode from "../../../services/hooks/useEditMode";
import useIsLoading from "../../../services/hooks/useIsLoading";
import { useTranslation } from "react-i18next";
import { DisagnosisTextType } from "../../molecules/DiagnosisText/DiagnosisText";
import { changeSidebarItemDiagnosisText } from "../../../services/api/sidebar.api";
import { sendToasterMessage } from "../../../services/helpers/commonHelpers";
import { useDispatch } from "react-redux";
import SpeechToTextPage from "../../pages/SpeechToTextPage/SpeechToTextPage";
import ProtectedComponent from "../../atoms/ProtectedComponent/ProtectedComponent";

export interface ReportBindingType {
  id: number | string;
  value: string | undefined;
  unit?: Unit;
  name: string;
  position: number;
  type: FieldTypeType;
  noteOptionValue?: string;
  visible?: boolean;
}

interface FormProps {
  sidebarItem: SidebarMenuItemType | undefined;
  fields: FieldType[];
  fieldsForReport: ReportFieldType[];
  tabs: TabType[];
  currentTabID: number;
  isDictationOpen: boolean;
  onSelectTab: (tabID: number) => void;
  onCreateTab: (tabID: number, index: number) => void;
  refreshData: () => void;
}

const Form = (props: FormProps) => {
  const {
    sidebarItem,
    fields,
    fieldsForReport,
    tabs,
    currentTabID,
    isDictationOpen,
    onSelectTab,
    onCreateTab,
    refreshData,
  } = props;

  const { isSidebarLoading } = useIsLoading();

  const [reportBinds, setReportBinds] = useState<ReportBindingType[]>([]);
  const [currentTab, setCurrentTab] = useState<number>(0);

  const isInEditMode = useIsEditMode();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    setCurrentTab(0);
    setReportBinds([]);
  }, [sidebarItem]);

  useEffect(() => {
    const report = bindFieldsToReport(fieldsForReport);
    setReportBinds(report);
  }, [fieldsForReport]);

  const handleSelectTab = (
    tabID: number,
    index: number,
    shouldChangePage: boolean,
  ) => {
    if (currentTab !== index) {
      setCurrentTab(index);
      if (shouldChangePage) {
        onSelectTab(tabID);
      }
    }
  };

  const handleFieldChange = (field: FieldType, value?: string) => {
    const currentItem = reportBinds.find((item) => item.id === field.fieldID);

    const newItem: ReportBindingType = {
      id: field.fieldID,
      value: value,
      position: field.position,
      unit: field.unit,
      type: field.type,
      name: field.name,
      noteOptionValue: currentItem?.noteOptionValue,
      visible: currentItem?.visible,
    };
    const newReportBinds = reportBinds.map((item) =>
      item.id === field.fieldID ? newItem : item,
    );

    setReportBinds(newReportBinds);
  };

  const handleNoteOptionChange = (field: FieldType, value?: string) => {
    const currentItem = reportBinds.find((item) => item.id === field.fieldID);
    const newItem: ReportBindingType = {
      id: field.fieldID,
      value: currentItem?.value,
      position: field.position,
      unit: field.unit,
      type: field.type,
      name: field.name,
      noteOptionValue: value,
      visible: currentItem?.visible,
    };

    const newReportBinds = reportBinds.map((item) =>
      item.id === field.fieldID ? newItem : item,
    );
    setReportBinds(newReportBinds);
  };

  const handleFieldVisibilityChange = (field: FieldType, value: boolean) => {
    const currentItem = reportBinds.find((item) => item.id === field.fieldID);

    const newItem: ReportBindingType = {
      id: field.fieldID,
      value: currentItem?.value,
      position: field.position,
      unit: field.unit,
      type: field.type,
      name: field.name,
      noteOptionValue: currentItem?.noteOptionValue,
      visible: value,
    };

    const newReportBinds = reportBinds.map((item) =>
      item.id === field.fieldID ? newItem : item,
    );
    setReportBinds(newReportBinds);
  };

  const handleReset = () => {
    const report = bindFieldsToReport(fieldsForReport);
    setCurrentTab(0);
    onSelectTab(tabs[0].tabID);
    setReportBinds(report);
  };

  const handleDiagnosisTextChange = (
    type: DisagnosisTextType,
    value: string,
  ) => {
    if (sidebarItem === undefined) {
      return;
    }
    changeSidebarItemDiagnosisText(
      dispatch,
      sidebarItem.sidebarItemID,
      type,
      value,
    ).then(() => {
      sendToasterMessage(dispatch, "Text bol úspešne uložený");
      refreshData();
    });
  };

  if (isDictationOpen) {
    return (
      <ProtectedComponent role="admin">
        <SpeechToTextPage />
      </ProtectedComponent>
    );
  }

  if (sidebarItem === undefined && !isSidebarLoading) {
    return (
      <div className="dui-welcome">
        <h1>Vitajte v DoktorUI</h1>
        <div>Začnite vytvorením prvého vyšetrenia:</div>
        <br />
        <div>1. Kliknite na tlačidlo "Editačný režim" v navigačnom paneli</div>
        <div>
          2. Vytvorte prvé vyšetrenie kliknutím na tlačidlo so znakom "+" v
          navigačnom paneli
        </div>
      </div>
    );
  }

  return sidebarItem !== undefined ? (
    <div className="dui-form">
      <div className="dui-form-fields">
        <div className="dui-form-title">
          <FormTitle
            title={sidebarItem.name}
            sidebarItemID={sidebarItem.sidebarItemID}
            warningText={sidebarItem.warningText}
            adviseText={sidebarItem.adviseText}
            onTitleChange={refreshData}
            onDiagnosisTextChange={handleDiagnosisTextChange}
          />
          {!isInEditMode && (
            <Button
              type="secondary"
              corners="rounded"
              icon={<RestoreIcon />}
              className="dui-form-reset-button"
              onClick={handleReset}
            >
              {t("newDiagnosis")}
            </Button>
          )}
        </div>
        <div className="dui-form-tabs">
          <Tabs
            items={tabs}
            onSelectTab={handleSelectTab}
            currentTab={currentTab}
            onCreateTab={onCreateTab}
            sidebarItemID={sidebarItem.sidebarItemID}
          />
        </div>
        <Fields
          items={fields}
          onFieldChange={handleFieldChange}
          onNoteOptionChange={handleNoteOptionChange}
          onFieldVisibilityChange={handleFieldVisibilityChange}
          reportBinds={reportBinds}
          tabID={currentTabID}
        />
      </div>
      <DoctorsReport
        sidebarItem={sidebarItem}
        reportBinds={reportBinds}
        onDiagnosisTextChange={handleDiagnosisTextChange}
      />
    </div>
  ) : null;
};

export default Form;
